import { DateTime, DateTimeUnit, DurationLike } from "luxon";

import { SitewideInterval } from "@intelligems/ig-types/dtos/sitewide-analytics";
import { getDaysFromHistory } from "@intelligems/ig-analytics/date";
import { ensureNumber } from "@intelligems/ig-utils/common";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SitewideAnalyticsPageName } from "@src/api/types/dashboards";

import {
  initialSitewideDashboardFiltersState,
  ISitewideDashboardFilters,
} from "../types/sitewideDashboardFilters";

// Used when the interval is changed while the time range has stayed intact.
// This will help show a more relevant default time range for the selected interval.
export const DEFAULT_TIME_DIFF_FOR_INTERVAL: Record<
  SitewideInterval,
  DurationLike
> = {
  "1 DAY": { days: 14 },
  "1 WEEK": { weeks: 12 },
  "1 MONTH": { months: 6 },
};

export const DEFAULT_START_TIME_FOR_INTERVAL: Record<
  SitewideInterval,
  DateTimeUnit
> = {
  "1 DAY": "day",
  "1 WEEK": "week", // Ensures we target the first day of the week
  "1 MONTH": "month", // Ensures we target the first day of the month
};

const slice = createSlice({
  name: "sitewideDashboardFilters",
  initialState: initialSitewideDashboardFiltersState,
  reducers: {
    clearSitewideDashboardFilters: (sitewideDashboardFilters) => {
      Object.assign(
        sitewideDashboardFilters,
        initialSitewideDashboardFiltersState
      );
    },
    setSitewideDashboardFilter: (
      sitewideDashboardFilters,
      action: PayloadAction<ISitewideDashboardFilters>
    ) => {
      Object.assign(sitewideDashboardFilters, action.payload);
    },
    setSitewidePageName: (
      draft,
      action: PayloadAction<{
        key: string;
        value: SitewideAnalyticsPageName;
      }>
    ) => {
      draft[action.payload.key] = action.payload.value;
      return draft;
    },

    updateSitewideDashboardFilter: (
      sitewideDashboardFilters,
      action: PayloadAction<{
        key: keyof ISitewideDashboardFilters;
        value: any;
      }>
    ) => {
      Object.assign(sitewideDashboardFilters, {
        ...sitewideDashboardFilters,
        [action.payload.key]: action.payload.value,
      });
    },

    // Used when time range (startTime/endTime) has not been modified but the interval changes.
    // It automatically sets a default relevant startTime depending on the interval.
    updateSitewideDashboardInterval: (
      sitewideDashboardFilters,
      action: PayloadAction<{
        interval: SitewideInterval;
        timezone?: string | null;
      }>
    ) => {
      if (
        !sitewideDashboardFilters.isTimeRangeUpdated &&
        action.payload.timezone
      ) {
        Object.assign(sitewideDashboardFilters, {
          ...sitewideDashboardFilters,
          ["interval"]: action.payload.interval,
          startTime: DateTime.now()
            .setZone(action.payload.timezone)
            .minus(DEFAULT_TIME_DIFF_FOR_INTERVAL[action.payload.interval])
            .startOf(DEFAULT_START_TIME_FOR_INTERVAL[action.payload.interval])
            .toISO(),
        });
      } else {
        Object.assign(sitewideDashboardFilters, {
          ...sitewideDashboardFilters,
          ["interval"]: action.payload.interval,
        });
      }
    },
  },

  selectors: {
    getSitewidePageName: (state) => state.pageName,
    selectedSitewideDashboardFilters: (state) => state,
  },
});

export const { getSitewidePageName, selectedSitewideDashboardFilters } =
  slice.selectors;

export const {
  updateSitewideDashboardFilter,
  updateSitewideDashboardInterval,
  setSitewideDashboardFilter,
  setSitewidePageName,
  clearSitewideDashboardFilters,
} = slice.actions;

export const sitewideDashboardFiltersReducer = slice.reducer;

export const getSitewideDiff = createSelector(
  selectedSitewideDashboardFilters,
  (filters) => {
    return getDaysFromHistory([], ensureNumber(filters.endTime || 0));
  }
);
